import type { DirectionalDrillingState, DirectionalDrillingStateParams } from '@repo/go-widgets';
import { observable, makeObservable, action } from 'mobx';

import { BaseWidgetEntityWithWellId } from 'src/entities/widget/BaseWidgetEntityWithWellId';
import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';
import { requireService } from 'src/packages/di';

interface DirectionalDrillingOptions {
  type: string;
  stateParams: Partial<DirectionalDrillingStateParams>;
  wellId: number | null;
}

export type TDirectionalDrillingSerializedInternalState = {
  stateParams: Partial<DirectionalDrillingStateParams>;
  wellId: number | null;
};

export class DirectionalDrillingWidgetEntity extends BaseWidgetEntityWithWellId {
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: {
      w: 150,
      h: 150,
    },
    maxInPixels: null,
    min: null,
    max: {
      w: 256,
      h: 100,
    },
  };
  @observable type: string;
  @observable state: DirectionalDrillingState | null = null;
  @observable wellId: number | null;
  @observable.ref stateParams: Partial<DirectionalDrillingStateParams>;
  @observable inActiveTab: boolean = false;
  @observable wellboreName: string | null = null;

  constructor(
    { type, stateParams, wellId }: DirectionalDrillingOptions,
    widgetOptions: TWidgetOptions,
    private readonly i18 = requireService('i18'),
  ) {
    super(widgetOptions);
    this.type = type;
    this.wellId = wellId;
    this.stateParams = stateParams;
    makeObservable(this);
  }

  get isControlWidget(): boolean {
    return false;
  }

  getNameT(): string {
    return 'widgets:DirectionalDrilling.title';
  }

  get title(): string {
    return this.i18.t(this.getNameT());
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setWellboreName(name: string): void {
    this.wellboreName = name;
  }

  @action.bound
  setFullscreen(value: boolean): void {
    this.isFullscreen = value;
  }

  @action.bound
  setState(state: DirectionalDrillingState | null): void {
    this.state = state;
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
    this.state?.setAppParams({
      active: this.inActiveTab,
    });
  }

  override destroy() {
    this.state?.destroy();
  }

  @action.bound
  setStateParams(stateParams: Partial<DirectionalDrillingStateParams>): void {
    this.stateParams = stateParams;
  }
}
